import { ReactElement, ReactNode } from 'react';
import { ContainerCard } from '../ContainerCard/ContainerCard';
import classes from './ResourceUsageCard.module.css';
import { formatNumber } from '../../utils/numberFormatters';
import {
  calculateResourceStats,
  formatPercentage,
  type ResourceUsage,
} from '../../utils/resourceCalculations/resourceCalculations';

type ResourceStatItemProps = {
  readonly label: string;
  readonly value: number;
};

const ResourceStatItem = ({ label, value }: ResourceStatItemProps): ReactElement => (
  <div className={classes.statItem}>
    <div className={classes.label}>{label}</div>
    <div className={classes.value}>{formatNumber(value)}</div>
  </div>
);

type ProgressBarProps = {
  readonly percentage: number;
  readonly variant?: 'default' | 'warning' | 'critical';
};

const ProgressBar = ({ percentage, variant = 'default' }: ProgressBarProps): ReactElement => {
  const variantClass = classes[`progressFill--${variant}`] || '';

  return (
    <div className={classes.progressBar}>
      <div
        className={`${classes.progressFill} ${variantClass}`}
        style={{ width: `${percentage}%` }}
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
};

type ResourceUsageCardProps = {
  readonly title: string;
  readonly icon?: ReactNode;
  readonly resource: ResourceUsage;
  readonly labels?: {
    readonly used?: string;
    readonly total?: string;
    readonly available?: string;
  };
};

/**
 * Determine progress bar variant based on percentage
 */
const getProgressVariant = (percentage: number): 'default' | 'warning' | 'critical' => {
  if (percentage >= 90) return 'critical';
  if (percentage >= 75) return 'warning';
  return 'default';
};

export const ResourceUsageCard = ({
  title,
  icon,
  resource,
  labels = {},
}: ResourceUsageCardProps): ReactElement => {
  const stats = calculateResourceStats(resource);
  const variant = getProgressVariant(stats.percentageUsed);

  const {
    used: usedLabel = 'Brukt',
    total: totalLabel = 'Totalt',
    available: availableLabel = 'Tilgjengelig',
  } = labels;

  return (
    <ContainerCard>
      <ContainerCard.Title icon={icon}>{title}</ContainerCard.Title>
      <ContainerCard.Body>
        <div className={classes.content}>
          <div className={classes.stats}>
            <ResourceStatItem label={usedLabel} value={stats.used} />
            <ResourceStatItem label={totalLabel} value={stats.total} />
            <ResourceStatItem label={availableLabel} value={stats.available} />
          </div>
          <ProgressBar percentage={stats.percentageUsed} variant={variant} />
          <div className={classes.progressLabel}>
            {formatPercentage(stats.percentageUsed)} brukt
          </div>
        </div>
      </ContainerCard.Body>
    </ContainerCard>
  );
};
